import {
  normalizeImportedAssetFileId,
  type ModelAssetFile,
} from '@puppycad/cad-core'
import type { FileInfo } from '@puppycad/proto'
import { CompleteGlbPackageWorkerClient } from './CompleteGlbPackageWorkerClient'

/** complete GLB 包默认最多允许的 ZIP entry 数量。 */
const DEFAULT_MAX_ENTRY_COUNT = 4096
/** complete GLB 包默认允许的单 entry 最大解压字节数。 */
const DEFAULT_MAX_SINGLE_ENTRY_BYTES = 512 * 1024 * 1024
/** complete GLB 包默认允许的总解压字节数。 */
const DEFAULT_MAX_TOTAL_UNCOMPRESSED_BYTES = 1024 * 1024 * 1024

/**
 * 完整 GLB 根节点包读取器。
 *
 * 该类只负责 ZIP I/O 与内存文件系统构造，不解释任何 CAD 或 GLTF 语义。
 */
export class CompleteGlbPackageReader {
  /** ZIP 解压能力；浏览器默认使用专用 Worker。 */
  private readonly decompressor: PuppyCADWeb.CompleteGlbPackageDecompressor

  /**
   * 创建完整 GLB 根节点包读取器。
   *
   * @param decompressor 可注入的解压能力，默认由专用 Worker 执行。
   */
  public constructor(
    decompressor: PuppyCADWeb.CompleteGlbPackageDecompressor = new CompleteGlbPackageWorkerClient(),
  ) {
    this.decompressor = decompressor
  }

  /**
   * 读取完整 GLB 根节点包。
   *
   * @param _input 根节点包文件与 ZIP 字节。
   * @returns 可供 imported-model loader 消费的内存文件系统。
   */
  public async read(
    input: PuppyCADWeb.CompleteGlbPackageReadInput,
  ): Promise<PuppyCADWeb.CompleteGlbPackageReadResult> {
    try {
      const rawEntries = await this.unzip(input)
      const entries = Object.entries(rawEntries)
        .map(([rawPath, bytes]) => ({
          path: this.normalizeSafeEntryPath(rawPath),
          bytes,
        }))
        .filter((entry) => entry.path.length > 0 && !entry.path.endsWith('/'))
      const normalizedPaths = new Set<string>()
      for (const entry of entries) {
        if (normalizedPaths.has(entry.path)) {
          throw new Error(`完整 GLB 节点包存在重复规范化路径: ${entry.path}`)
        }
        normalizedPaths.add(entry.path)
      }
      const glbEntries = entries.filter((entry) => entry.path.toLowerCase().endsWith('.glb'))

      if (glbEntries.length !== 1) {
        throw new Error(
          `完整 GLB 节点包必须包含且仅包含一个 GLB 文件: ${input.packageFile.fileId}, count=${glbEntries.length}`,
        )
      }

      const projectFiles = entries.map((entry) => (
        this.createVirtualFile(input.packageFile, entry.path, entry.bytes)
      ))
      const bytesByFileId = new Map<string, Uint8Array>()
      projectFiles.forEach((file, index) => {
        bytesByFileId.set(file.fileId, entries[index].bytes)
      })
      const sourceGlbEntryIndex = entries.indexOf(glbEntries[0])
      const sourceGlbFile = projectFiles[sourceGlbEntryIndex]

      if (!sourceGlbFile) {
        throw new Error(`完整 GLB 节点包无法建立 GLB 虚拟文件: ${glbEntries[0].path}`)
      }

      input.assertCurrent?.()
      return {
        sourceGlbFile,
        projectFiles,
        readFileBytes: async (file) => {
          const bytes = bytesByFileId.get(normalizeImportedAssetFileId(file.fileId))
          if (!bytes) {
            throw new Error(`完整 GLB 节点包缺少文件: ${file.fileId}`)
          }
          // 只在调用方实际读取单个文件时复制，避免解包后立即产生第二份整包内存。
          return new Uint8Array(bytes)
        },
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      throw new Error(
        `完整 GLB 节点包读取失败: ${reason}, packageFileId=${input.packageFile.fileId}, packageUuid=${input.packageFile.uuid || 'none'}`,
        { cause: error },
      )
    }
  }

  /**
   * 使用 fflate 异步入口解压 ZIP；执行器当前为性能压测临时放行大小限制，仍限制 entry 数量。
   *
   * @param input 外层包字节、请求断言和资源限制。
   * @returns ZIP 原始路径到解压字节的映射。
   */
  private async unzip(
    input: PuppyCADWeb.CompleteGlbPackageReadInput,
  ): Promise<Record<string, Uint8Array>> {
    const limits = input.limits ?? {
      maxEntryCount: DEFAULT_MAX_ENTRY_COUNT,
      maxSingleEntryBytes: DEFAULT_MAX_SINGLE_ENTRY_BYTES,
      maxTotalUncompressedBytes: DEFAULT_MAX_TOTAL_UNCOMPRESSED_BYTES,
    }
    return await this.decompressor.decompress({
      bytes: input.bytes,
      limits,
      assertCurrent: input.assertCurrent,
    })
  }

  /**
   * 在执行公共路径规范化之前拒绝 ZIP 路径逃逸。
   *
   * 公共规范化函数会折叠 `..`，适合解析可信引用，但 ZIP entry 属于外部输入；
   * 若先规范化再校验会丢失路径穿越证据，因此必须先检查原始 segment。
   *
   * @param rawPath ZIP 中的原始 entry 路径。
   * @returns 可用于内存文件系统的规范化相对路径。
   */
  private normalizeSafeEntryPath(rawPath: string): string {
    const slashPath = rawPath.replaceAll('\\', '/').trim()
    if (
      slashPath.startsWith('/')
      || /^[a-zA-Z]:\//.test(slashPath)
      || slashPath.split('/').some((segment) => segment === '..')
    ) {
      throw new Error(`完整 GLB 节点包包含不安全路径: ${rawPath}`)
    }

    return normalizeImportedAssetFileId(slashPath)
  }

  /**
   * 创建 ZIP entry 对应的虚拟文件描述。
   *
   * @param packageFile 根节点包文件。
   * @param entryPath 已规范化的 ZIP entry 路径。
   * @param bytes entry 字节。
   * @returns imported-model loader 可消费的文件描述。
   */
  private createVirtualFile(
    packageFile: ModelAssetFile,
    entryPath: string,
    bytes: Uint8Array,
  ): FileInfo {
    const packageSegments = normalizeImportedAssetFileId(packageFile.fileId)
      .split('/')
      .filter(Boolean)
    packageSegments.pop()
    const packageBaseDirectory = packageSegments.join('/')
    const fileId = packageBaseDirectory
      ? `${packageBaseDirectory}/${entryPath}`
      : entryPath
    const normalizedLowerPath = entryPath.toLowerCase()
    const lastDotIndex = normalizedLowerPath.lastIndexOf('.')

    return {
      fileId,
      uuid: `${packageFile.uuid || packageFile.fileId}::zip-entry::${entryPath}`,
      kind: lastDotIndex >= 0 ? normalizedLowerPath.slice(lastDotIndex) : '',
      size: BigInt(bytes.byteLength),
    } as FileInfo
  }
}
